// ** React Imports
import { useState, useEffect, useContext, useMemo } from 'react'

// ** Custom Components
import Avatar from '@components/avatar'

// ** Third Party Components
import classnames from 'classnames'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { MoreVertical, Send, Image } from 'react-feather'

// ** Reactstrap Imports
import { Card, CardHeader, Form, Label, InputGroup, Input, InputGroupText, Button, Spinner } from 'reactstrap'

// ** Styles
import '@styles/base/pages/app-chat-list.scss'
import css from '@mstyle/style.module.css'
// import typingCss from '@mstyle/chat.module.css'

import { useTranslation } from 'react-i18next'

import { AuthContext } from "@context/AuthContext"
import useApi from '@hooks/useApi'
import useLoader from '@hooks/useLoader';
import SocketActions from "./actions"
import useToast from "@hooks/useToast";
import useWs from '@hooks/useWS'

import './style.scss'

const CardChat = (props) => {
    // ** Contexts
    const user = useContext(AuthContext)
    const addToast = useToast()
    const { t } = useTranslation()

    // ** API
    const ChatApi = useApi().chat
    const { isLoading, fetchData } = useLoader({})
    const { isLoading: isLoadingSmall, fetchData: fetchDataSmall, Loader } = useLoader({isSmall:true})

    // ** States
    const [msg, setMsg] = useState('')
    const [chatRef, setChatRef] = useState(null)
    const [contact, setContact] = useState('')
    const [roomId, setRoomId] = useState(null)
    const [inputType, setInputType] = useState('text')
    const [files, setFiles] = useState('')
    const [myTyping, setMyTyping] = useState(false)
    const [isButtom, setIsButtom] = useState(true)

    // ** const values
    let lastTop = 0
    let isRendered = false
    const imageFileTypes = ['jpeg', 'jpg', 'png', 'gif']

    const formatData = (prev, newData) =>
    {
        const newMsg = {...prev}

        newMsg.chat.push({
            chatId: newData.chatId,
            message: newData.message,
            timestamp: newData.timestamp,
            files: newData?.files ? newData.files : null,
            user: newData.user,
        })
        return prev
    }

    const {data, setData, isSocketConnected, socketSend} = useWs({
        roomId,
        action: {
            /** Тухайн өрөөнд ирсэн socket чат дата хадгалах */
            [SocketActions.MESSAGE]: {
                initData: {chat: []},
                format: formatData
            },
            /** Чат бичиж байгаа төлөвийн мэдээллийг сонсох нь */
            [SocketActions.TYPING]: {
                initData: false,
                format: (prev, newData) =>
                {
                    if (user.userDetail.id !== newData.user)
                    {
                        return newData.typing
                    }
                    else
                    {
                        return prev
                    }
                }
            },
            /** Online хэрглэгчийн жагсаалтын сонсох нь */
            [SocketActions.ONLINE_USER]: {
                initData: [],
                format: (prev, newData) =>
                {
                    return newData?.userList ?? []
                }
            },
        },
    })

    const chatData = data[SocketActions.MESSAGE]
    const typing = data[SocketActions.TYPING]
    const onlineUsers = data[SocketActions.ONLINE_USER]
    /** hook set data */
    const setChatData = (data) =>
    {
        setData((prev) =>
        {
            prev['message'].chat = data?.chat.concat(prev['message']?.chat)
            return prev
        })
    }

    /** Чат өрөөний хэрэглэгчийн мэдээллийг хадгалах */
    useEffect(() =>
    {
        for (let index in props?.data?.member)
        {
            if(user?.userDetail?.id !== props?.data?.member[index].id)
            {
                setContact(
                    {
                        ...props?.data?.member[index]?.student,
                        id: props?.data?.member[index]?.id
                    }
                )
            }
        }
        setRoomId(props?.data?.roomId)
        getChatData(props?.data?.roomId, fetchData)

    },[props?.data?.roomId])

    /** socket connect хийгдэх үед ашиглана  */
    useEffect(
        () =>
        {
            if(isSocketConnected)
            {
                /** Online хэрэлэгчдийн жагсаалтыг авах */
                socketSend({
                    action: SocketActions.GET_ONLINE_USER,
                    user: user?.userDetail?.id,
                    roomId: props?.data?.roomId,
                })
            }
        },
        [isSocketConnected, props?.data?.roomId]
    )

    /** back-аас чат дата авах */
    const getChatData = async (roomId, fetchData) =>
    {
        if(!roomId)
            return

        let lastId = 0
        if (chatData?.chat?.length > 0)
        {
            lastId = chatData?.chat[0]?.chatId
        }
        const {success, data} = await fetchData(ChatApi.getChatData(roomId, lastId))
        if(success) {
            setChatData(data)
        }
    }

    /** Input дээр дарсан үед тухайн чатыг уншсан гэж үзнэ typing бичиж байгааг илгэнэ */
    const readedFn = () =>
    {
        socketSend({
            action: SocketActions.READED,
            user: user?.userDetail?.id,
            roomId: roomId,
        })
    }

    //** Formats chat data based on sender
    const formattedChatData = () => {
        let chatLog = []
        if (chatData) {
            chatLog = chatData.chat
        }
        const formattedChatLog = []

        if(chatLog && chatLog.length > 0)
        {
            let chatMessageuser = chatLog[0] ? chatLog[0].user : undefined
            let msgGroup = {
                user: chatMessageuser,
                messages: []
            }
            chatLog.forEach((msg, index) => {
            if (chatMessageuser === msg.user) {
                msgGroup.messages.push({
                    chatId: msg.chatId,
                    msg: msg.message,
                    time: msg.timestamp,
                    file: msg.files,
                    defaultId: msg?.defaultId ? msg.defaultId : null
                })
            } else {
                chatMessageuser = msg.user
                formattedChatLog.push(msgGroup)
                msgGroup = {
                    user: msg.user,
                    messages: [
                        {
                            chatId: msg.chatId,
                            msg: msg.message,
                            time: msg.timestamp,
                            file: msg.files,
                            defaultId: msg?.defaultId ? msg.defaultId : null
                        }
                    ]
                }
            }
            if (index === chatLog.length - 1) formattedChatLog.push(msgGroup)
            })
        }
        return formattedChatLog
    }

    /** Render Чат */
    const renderChats = useMemo(() => {
        return formattedChatData().map((item, index) => {
            return (
                <div
                    key={index}
                    className={classnames('chat', {
                        'chat-left': item.user !== user?.userDetail?.id
                    })}
                    >
                    <div className='chat-avatar'>
                        <Avatar
                            className='box-shadow-1 cursor-pointer'
                            img={item.user === user?.userDetail?.id ? user?.userDetail?.student?.image : contact?.image}
                        />
                    </div>

                    <div className='chat-body'>
                        {item.messages.map((chat, index) => {
                            const file = chat?.file ? chat?.file : null
                            let fileName = undefined
                            let splitedFile = undefined
                            if(file)
                            {
                                fileName = file.toString().split("/").pop()
                                splitedFile = fileName?.split(".").pop()
                            }

                            return (
                                <div key={index} className='chat-content' id = {chat.defaultId}>
                                    <p>{chat.msg}</p>
                                    {
                                        file
                                        ?
                                            <a target={'_blank'} href={file}>{
                                                imageFileTypes.includes(splitedFile)
                                                ?
                                                <img src={file} height="100"></img>
                                                :
                                                <i className="fas fa-file text-info">{" " + fileName}</i>
                                            }</a>
                                        :
                                            null
                                    }
                                </div>
                            )
                        }
                        )}
                    </div>
                </div>
            )
        })
    }, [chatData.chat.length, chatData.chat, msg])

    //** Scroll to chat bottom
    const scrollToBottom = () => {
        chatRef.scrollTop = Number.MAX_SAFE_INTEGER
    }

    useMemo(() => {
        if(chatRef !== null)
        {
            if(!isButtom && chatRef.scrollTop === 0)
            {
                lastTop = chatRef.scrollHeight
                isRendered = true
            }
            else
            {
                lastTop = chatRef.scrollTop
                isRendered = false
            }
        }
    }, [chatRef, chatData.chat.length])

    useEffect(() => {
        if (chatRef !== null)
        {
            if(isButtom)
                scrollToBottom()
            else if (isRendered)
            {
                chatRef.scrollTop = chatRef.scrollHeight - lastTop
            }
            else
            {
                scrollToBottom()
            }
            isRendered = false
        }
    }, [chatRef, chatData.chat.length, typing, isSocketConnected])

    const handleScroll = (e) => {
        const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
        if (e?.target?.scrollTop === 0) {
            getChatData(roomId, fetchDataSmall)
        }
        if(bottom)
        {
            setIsButtom(true)
        }
        else if (isButtom && !bottom)
        {
            setIsButtom(false)
        }
    }

    /** Чат илгээх socket */
    const handleSendMsg = e => {
        e.preventDefault()
        if (files)
        {
            const reader = new FileReader()

            reader.onload = function(event) {
                const fileData = event.target.result.split(',')[1];
                socketSend({
                    action: SocketActions.MESSAGE_FILE,
                    data: fileData,
                    name: files.name,
                    user: user?.userDetail?.id,
                    roomId: roomId
                })
            };
            reader.readAsDataURL(files);
            setFiles(null)
        }
        else if (msg.trim().length) {
            socketSend({
                action: SocketActions.MESSAGE,
                message: msg,
                user: user?.userDetail?.id,
                roomId: roomId,
            })
            setMsg('')
        }
        socketSend({
            action: SocketActions.TYPING,
            typing: false,
            user: user?.userDetail?.id,
            roomId: roomId,
        })
        setMyTyping(false)
    }

    /** Input onchange handler шүү */
    const handleInputChange = (e) =>
    {
        setMsg(e.target.value)
        if(!myTyping && e.target.value.length > 0)
        {
            socketSend({
                action: SocketActions.TYPING,
                typing: true,
                user: user?.userDetail?.id,
                roomId: roomId,
            })
            setMyTyping(true)
        }
        else if (myTyping && e.target.value.length === 0)
        {
            socketSend({
                action: SocketActions.TYPING,
                typing: false,
                user: user?.userDetail?.id,
                roomId: roomId,
            })
            setMyTyping(false)
        }
    }

    function handleFile() {
        setInputType('image/*')
    }

    /** Input дээр файл авах  */
    function onChangeFile(e) {
        const files = e.target.files[0]
        if (files) {
            /** Файлын хэмжээ 10mb дээгш байвал warning toast харуулна */
            if (files.size > 10485760)
            {
                addToast(
                    {
                        type: 'warning',
                        text: 'Хамгийн ихдээ 10MB хэмжээтэй файл оруулна.'
                    }
                )
            }
            else
            {
                setFiles(files)
            }
        }
        e.target.value = ''
    }

    function handleDelete()
    {
        setFiles(null)
    }

    return (
        <Card className='chat-widget mb-0' style={{ height: '100%' }}>
            <CardHeader>
                <div className='d-flex align-items-center'>
                    <Avatar
                        status= {
                            onlineUsers.includes(contact?.id)
                            ?
                                'online'
                            :
                                "offline"
                        }
                        className='me-2'
                        img={contact ? contact.image : null}
                        imgHeight='34'
                        imgWidth='34'
                    />
                    <h5 className='mb-0'>{contact.fullName}</h5>
                </div>
                <MoreVertical size={18} className='cursor-pointer' />
            </CardHeader>
            <div className='chat-app-window'>
                <PerfectScrollbar
                    containerRef={el => setChatRef(el)}
                    className='user-chats scroll-area'
                    options={{ wheelPropagation: false }}
                    onScroll={el => handleScroll(el)}
                >
                    {
                        isLoading
                        ?
                            <div className='suspense-loader'>
                                <Spinner size='bg'/>
                                <span className='ms-50'>{t('Түр хүлээнэ үү...')}</span>
                            </div>
                        :
                            <>
                            <div className='chats'>
                                {isLoadingSmall &&
                                    <div  style={{ textAlign: 'center'}}>
                                        {Loader}
                                    </div>}
                                {renderChats}
                            </div>
                                {typing && (
                                <div className='chat-bubble mb-1'>
                                    <div className='typing'>
                                        <div className='dot'></div>
                                        <div className='dot'></div>
                                        <div className='dot'></div>
                                    </div>
                                </div>
                                )}
                                {
                                    isSocketConnected
                                    ?
                                    null
                                    :
                                    <div style={{ justifyContent: 'center'}}>
                                        <p style={{ fontSize: 15, textAlign: 'center',color: 'red' }}>---Холболт салсан байна---</p>
                                    </div>
                                }
                            </>
                    }
                </PerfectScrollbar>
                <Form className='chat-app-form' onSubmit={e => handleSendMsg(e)}>
                    <InputGroup className='input-group-merge me-1 form-send-message'>
                        <InputGroupText>
                            <Label className='attachment-icon mb-0' for='attach-doc'>
                                <Image className='cursor-pointer text-secondary' size={14} onClick={() => handleFile()} />
                                <Input type='file' id='attach-doc' onChange={(e) => onChangeFile(e)} hidden accept='image/*' />
                            </Label>
                        </InputGroupText>
                        {
                            files ?
                                <Button tag={Label} className={` d-inline-block text-truncate ${css.cchatMobile}`} size='sm' color='primary' onClick={handleDelete}  style={{width: '80%'}}>
                                            {`${files.name} `}
                                </Button>
                            :
                                <Input
                                    value={msg}
                                    type={inputType}
                                    maxLength={200}
                                    className='border-0'
                                    onClick={readedFn}
                                    onChange={handleInputChange}
                                    placeholder='Чатаа бичнэ үү'
                                />
                        }
                    </InputGroup>
                    <Button className='send' color='primary'>
                        <Send size={14} className='d-lg-none' />
                        <span className='d-none d-lg-block'>Илгээх</span>
                    </Button>
                </Form>
            </div>
        </Card>
    )
}

export default CardChat
